/**
 * Cuerpo del modal de un pedido (ADM-09): la linea de estado arriba y debajo
 * una tarjeta plegable por tema (lo comprado, la boleta o factura, cliente,
 * entrega, pago y correos). Lo que no aplica (facturacion con boleta,
 * direccion con retiro, pago sin pagar) no se dibuja: un bloque de guiones
 * obliga a leerlo para descubrir que no dice nada.
 *
 * Un pedido pagado trae en la tarjeta del documento el formulario para subirlo
 * (DOC-01): todo lo que hace falta para emitirlo esta en el mismo modal.
 */
import type { ReactNode } from 'react';

import type { DireccionFacturacion, Pedido, PedidoAdminDetalle } from '@bandu/compartido';

import { textoEntrega } from '../../utilidades/entrega';
import { fechaHoraCompleta, numero, pesos } from '../../utilidades/formato';
import {
  ESTADO_PAGO,
  estadoCorreo,
  nombreDocumento,
  rotuloDocumento,
  TIPO_CORREO,
  unidadesPedido,
} from '../../utilidades/textos-pedido';
import { FormularioDocumentoPedido } from './FormularioDocumentoPedido';
import { ItemsPedido } from './ItemsPedido';
import { PasosPedido } from './PasosPedido';
import { TarjetaPedido } from './TarjetaPedido';

export interface DetallePedidoProps {
  pedido: PedidoAdminDetalle;
  volver: string;
  onDocumentoSubido(pedido: Pedido): void;
}

interface DatoProps {
  nombre: string;
  children: ReactNode;
}

function Dato({ nombre, children }: DatoProps) {
  return (
    <div>
      <dt>{nombre}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Fecha({ iso }: { iso: string }) {
  return <time dateTime={iso}>{fechaHoraCompleta(iso)}</time>;
}

function direccionEnLinea(pedido: PedidoAdminDetalle): string | null {
  const direccion = pedido.direccion;
  if (direccion === null) return null;
  const calle = [direccion.calle, direccion.numero].join(' ');
  return direccion.departamento !== null ? `${calle}, depto. ${direccion.departamento}` : calle;
}

function direccionFacturacionEnLinea(direccion: DireccionFacturacion): string {
  const calle = [direccion.calle, direccion.numero].join(' ');
  const conDepto = direccion.departamento !== null ? `${calle}, depto. ${direccion.departamento}` : calle;
  return `${conDepto}, ${direccion.comuna}, ${direccion.region}`;
}

/**
 * Por `tipoEntrega` y no por si hay direccion: una entrega a coordinar
 * tampoco la trae, y no cuesta "gratis": se cobra aparte, por telefono.
 */
function costoEntrega(pedido: PedidoAdminDetalle): string {
  if (pedido.tipoEntrega === 'coordinar') return 'Se acuerda por telefono';
  return pedido.costoDespacho > 0 ? pesos(pedido.costoDespacho) : 'Gratis';
}

/**
 * Si la boleta o factura le llego al cliente: el ultimo correo del documento
 * (un documento reemplazado manda otro). Sin documento no se dibuja nada.
 */
function EnvioDocumento({ pedido }: { pedido: PedidoAdminDetalle }) {
  if (pedido.urlDocumento === null) return null;

  const correo = pedido.correos.filter((fila) => fila.tipo === 'documento_emitido').at(-1);
  if (correo === undefined) {
    return <Dato nombre="Envio al cliente">Sin correo registrado</Dato>;
  }

  const estado = estadoCorreo(correo);
  return (
    <Dato nombre="Envio al cliente">
      <span className={estado.clase}>{estado.texto}</span>{' '}
      {correo.enviadoEn !== null ? (
        <span className="partible">
          el <Fecha iso={correo.enviadoEn} />, a {correo.destinatario}
        </span>
      ) : null}
    </Dato>
  );
}

function DatosDocumento({ pedido }: { pedido: PedidoAdminDetalle }) {
  const documento = rotuloDocumento(pedido);

  return (
    <dl className="datos-pedido">
      <Dato nombre="Pedido">{nombreDocumento(pedido)}</Dato>
      <Dato nombre="Estado">
        <span className={documento.clase}>{documento.texto}</span>
      </Dato>
      {pedido.urlDocumento !== null ? (
        <Dato nombre="Archivo">
          <a href={pedido.urlDocumento} target="_blank" rel="noopener noreferrer">
            Abrir el PDF
          </a>
        </Dato>
      ) : null}
      {pedido.fechaDocumento !== null ? (
        <Dato nombre="Subido el">
          <Fecha iso={pedido.fechaDocumento} />
        </Dato>
      ) : null}
      <EnvioDocumento pedido={pedido} />
    </dl>
  );
}

function DatosCliente({ pedido }: { pedido: PedidoAdminDetalle }) {
  return (
    <dl className="datos-pedido">
      <Dato nombre="Nombre">{pedido.contacto.nombre}</Dato>
      <Dato nombre="Correo">
        <a href={`mailto:${pedido.contacto.email}`}>{pedido.contacto.email}</a>
      </Dato>
      <Dato nombre="Telefono">
        <a href={`tel:${pedido.contacto.telefono.replace(/\s/g, '')}`}>{pedido.contacto.telefono}</a>
      </Dato>
      {pedido.rutComprador !== null ? <Dato nombre="RUT">{pedido.rutComprador}</Dato> : null}
      <Dato nombre="Promociones">
        {pedido.aceptaPromociones ? 'Acepta recibirlas' : 'No las acepto'}
      </Dato>
      {pedido.facturacion !== null ? (
        <>
          <Dato nombre="RUT empresa">{pedido.facturacion.rut}</Dato>
          <Dato nombre="Razon social">{pedido.facturacion.razonSocial}</Dato>
          <Dato nombre="Giro">{pedido.facturacion.giro}</Dato>
          {pedido.facturacion.direccion !== null ? (
            <Dato nombre="Direccion de facturacion">
              <span className="partible">{direccionFacturacionEnLinea(pedido.facturacion.direccion)}</span>
            </Dato>
          ) : null}
        </>
      ) : null}
    </dl>
  );
}

function DatosEntrega({ pedido }: { pedido: PedidoAdminDetalle }) {
  return (
    <dl className="datos-pedido">
      <Dato nombre="Tipo">{textoEntrega(pedido.tipoEntrega)}</Dato>
      {pedido.tipoEntrega === 'coordinar' ? (
        <Dato nombre="Que hacer">Llamar al cliente para acordar la forma y el costo</Dato>
      ) : null}
      {pedido.direccion !== null ? (
        <>
          <Dato nombre="Direccion">{direccionEnLinea(pedido)}</Dato>
          <Dato nombre="Comuna">
            {pedido.direccion.comuna}, {pedido.direccion.region}
          </Dato>
          {pedido.direccion.referencia !== null ? (
            <Dato nombre="Referencia">{pedido.direccion.referencia}</Dato>
          ) : null}
        </>
      ) : null}
      <Dato nombre="Costo">{costoEntrega(pedido)}</Dato>
    </dl>
  );
}

function DatosPago({ pedido }: { pedido: PedidoAdminDetalle }) {
  const pago = ESTADO_PAGO[pedido.estado];
  const { pago: webpay } = pedido;

  return (
    <dl className="datos-pedido">
      <Dato nombre="Estado">
        <span className={pago.clase}>{pago.texto}</span>
      </Dato>
      {pedido.pagadoEn !== null ? (
        <Dato nombre="Pagado el">
          <Fecha iso={pedido.pagadoEn} />
        </Dato>
      ) : null}
      {webpay.tipoPago !== null ? (
        <Dato nombre="Medio">
          {webpay.tipoPago}
          {webpay.cuotas !== null && webpay.cuotas > 1 ? `, ${numero(webpay.cuotas)} cuotas` : ''}
        </Dato>
      ) : null}
      {webpay.ultimosDigitosTarjeta !== null ? (
        <Dato nombre="Tarjeta">
          <span className="mono">**** {webpay.ultimosDigitosTarjeta}</span>
        </Dato>
      ) : null}
      {webpay.codigoAutorizacion !== null ? (
        <Dato nombre="Autorizacion">
          <span className="mono">{webpay.codigoAutorizacion}</span>
        </Dato>
      ) : null}
      {webpay.montoAutorizado !== null ? (
        <Dato nombre="Monto autorizado">{pesos(webpay.montoAutorizado)}</Dato>
      ) : null}
      {webpay.ordenCompra !== null ? (
        <Dato nombre="Orden de compra">
          <span className="mono">{webpay.ordenCompra}</span>
        </Dato>
      ) : null}
      {pedido.tokenTransaccion !== null ? (
        <Dato nombre="Token">
          <span className="mono partible">{pedido.tokenTransaccion}</span>
        </Dato>
      ) : null}
    </dl>
  );
}

function ListaCorreos({ pedido }: { pedido: PedidoAdminDetalle }) {
  if (pedido.correos.length === 0) {
    return <p className="sub">Este pedido no tiene correos registrados.</p>;
  }

  return (
    <ul className="correos-pedido">
      {pedido.correos.map((correo) => {
        const estado = estadoCorreo(correo);
        return (
          <li key={correo.id}>
            <div>
              <span className="n">{TIPO_CORREO[correo.tipo]}</span>
              <span className="s partible">
                A {correo.destinatario} · <Fecha iso={correo.enviadoEn ?? correo.creadoEn} />
              </span>
              {correo.ultimoError !== null && correo.estado !== 'enviado' ? (
                <span className="s texto-critico partible">{correo.ultimoError}</span>
              ) : null}
            </div>
            <span className={estado.clase}>{estado.texto}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function DetallePedido({ pedido, volver, onDocumentoSubido }: DetallePedidoProps) {
  // Los correos son la bitacora, no el pedido: nacen plegados salvo que alguno haya fallado.
  const correoFallido = pedido.correos.some((correo) => correo.estado === 'fallido');
  const nombre = nombreDocumento(pedido).toLowerCase();

  return (
    <div className="detalle-pedido">
      <PasosPedido pedido={pedido} />

      <div className="tarjetas-pedido">
        <TarjetaPedido icono="caja" titulo="Productos" detalle={unidadesPedido(pedido)} ancha>
          <ItemsPedido pedido={pedido} volver={volver} />
        </TarjetaPedido>

        <TarjetaPedido icono="factura" titulo="Boleta o factura" ancha>
          <DatosDocumento pedido={pedido} />
          {/* Solo un pedido pagado lleva documento; el servidor exige lo mismo. */}
          {pedido.estado === 'pagado' ? (
            <section className="subir-documento-pedido" aria-labelledby="pedido-subir-documento">
              <h4 id="pedido-subir-documento">
                {pedido.folioDocumento !== null ? `Reemplazar la ${nombre}` : `Subir la ${nombre}`}
              </h4>
              <FormularioDocumentoPedido
                key={`${pedido.id}-${pedido.folioDocumento ?? ''}`}
                pedido={pedido}
                onSubido={onDocumentoSubido}
              />
            </section>
          ) : null}
        </TarjetaPedido>

        <TarjetaPedido icono="persona" titulo="Cliente">
          <DatosCliente pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido icono={pedido.tipoEntrega === 'retiro' ? 'tienda' : 'camion'} titulo="Entrega">
          <DatosEntrega pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido icono="tarjeta" titulo="Pago" detalle="(Webpay)">
          <DatosPago pedido={pedido} />
        </TarjetaPedido>

        <TarjetaPedido
          icono="correo"
          titulo="Correos"
          detalle={`(${numero(pedido.correos.length)})`}
          abierta={correoFallido}
        >
          <ListaCorreos pedido={pedido} />
        </TarjetaPedido>
      </div>
    </div>
  );
}
